import { useState, useEffect } from "react";
import {
  FileText,
  CheckCircle,
  User,
  Hash,
  Droplet,
  X,
  Building,
  Package,
  Activity,
} from "lucide-react";
import { fetchInOutData, updateShipment, STORAGE_KEYS } from "../services/api";

// User Type Detection
const getUserTypeFlags = async () => {
  try {
    const bpartnerData = localStorage.getItem(STORAGE_KEYS.B_PARTNER);
    if (bpartnerData) {
      const data = JSON.parse(bpartnerData);
      return {
        isBDO: !!data.isbdo,
        isHospital: !!data.isHospital,
        isCollection: !!(data.IsEmployee || data.IsVolunteer),
        isPublicUser: !!(
          data.isdonor &&
          !data.isbdo &&
          !data.isHospital &&
          !(data.IsEmployee || data.IsVolunteer)
        ),
      };
    }
  } catch (err) {
    console.error("Error parsing bpartner data:", err);
  }
  return {
    isBDO: false,
    isHospital: false,
    isCollection: false,
    isPublicUser: false,
  };
};

const InOutRecords = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [readRecords, setReadRecords] = useState(new Set());
  const [updatingRecords, setUpdatingRecords] = useState(new Set());
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Modal state
  const [showMarkAsUsedModal, setShowMarkAsUsedModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [modalPatientName, setModalPatientName] = useState("");
  const [modalAge, setModalAge] = useState("");
  const [userType, setUserType] = useState({ isBDO: false, isHospital: false });

  useEffect(() => {
    loadInOutRecords();
    loadUserType();
  }, []);

  const loadUserType = async () => {
    try {
      const userTypeFlags = await getUserTypeFlags();
      setUserType(userTypeFlags);
    } catch (err) {
      console.error("Failed to load user type:", err);
    }
  };

  const loadInOutRecords = async () => {
    try {
      setLoading(true);
      // Fetch real in/out records from the backend
      const realRecords = await fetchInOutData();
      setRecords(realRecords);
    } catch (error) {
      console.error("Failed to load in/out records:", error);
    } finally {
      setLoading(false);
    }
  };

  // Parse ASI field to extract blood bag details
  // Format: "1_Cryo_A_Positive_2026-08-06" -> Blood Bag No, Product, Blood Group, RH, Expiry Date
  const parseASI = (asi) => {
    if (!asi) return { bloodBagNo: "", product: "", bloodGroupRH: "" };

    const parts = asi.split("_");
    if (parts.length < 4)
      return { bloodBagNo: "", product: "", bloodGroupRH: "" };

    const bloodBagNo = parts[0];
    const product = parts[1];
    const bloodGroup = parts[2]; // e.g., "A", "O", "B", "AB"
    const rhFactor = parts[3]; // e.g., "Positive", "Negative"

    // Combine blood group and RH factor for display
    const bloodGroupRH = `${bloodGroup} ${rhFactor}`;
    // We don't need the expiry date (parts[4])

    return { bloodBagNo, product, bloodGroupRH };
  };

  const handleMarkAsUsed = (record) => {
    // Open modal with record data
    setSelectedRecord(record);
    setModalPatientName(record.patientname || "");
    setModalAge(record.age || "");
    setShowMarkAsUsedModal(true);
  };

  const handleConfirmMarkAsUsed = async () => {
    if (!selectedRecord) return;

    try {
      setError(null);
      setSuccessMessage(null);
      setUpdatingRecords(
        (prev) => new Set([...prev, selectedRecord.m_inoutline_id])
      );

      // Call the updateShipment API with modal data
      const result = await updateShipment(
        modalAge,
        selectedRecord.m_inoutline_id,
        modalPatientName
      );

      // Mark as read/used locally after successful API call
      setReadRecords(
        (prev) => new Set([...prev, selectedRecord.m_inoutline_id])
      );

      // Show success message
      setSuccessMessage(
        `Blood shipment has been successfully marked as used for patient ${modalPatientName} (Age: ${modalAge}).`
      );

      // Auto-hide success message after 5 seconds
      setTimeout(() => setSuccessMessage(null), 5000);

      // Close modal and reset state
      setShowMarkAsUsedModal(false);
      setSelectedRecord(null);
      setModalPatientName("");
      setModalAge("");
    } catch (error) {
      console.error("Failed to mark as used:", error);
      setError(error.message || "Failed to update shipment. Please try again.");
    } finally {
      setUpdatingRecords((prev) => {
        const newSet = new Set(prev);
        newSet.delete(selectedRecord.m_inoutline_id);
        return newSet;
      });
    }
  };

  const handleCloseModal = () => {
    setShowMarkAsUsedModal(false);
    setSelectedRecord(null);
    setModalPatientName("");
    setModalAge("");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading in/out records...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center mb-2">
            {userType.isHospital ? (
              <Package className="h-6 w-6 text-red-600 mr-2" />
            ) : userType.isBDO ? (
              <Activity className="h-6 w-6 text-green-600 mr-2" />
            ) : null}
            <h1 className="text-2xl font-bold text-gray-900">In/Out Records</h1>
          </div>
          <p className="text-gray-600">
            Blood inventory shipment records and patient information
          </p>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-green-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-green-800">{successMessage}</p>
            </div>
            <div className="ml-auto pl-3">
              <div className="-mx-1.5 -my-1.5">
                <button
                  onClick={() => setSuccessMessage(null)}
                  className="inline-flex bg-green-50 rounded-md p-1.5 text-green-500 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-green-50 focus:ring-green-600"
                >
                  <span className="sr-only">Dismiss</span>
                  <svg
                    className="h-3 w-3"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-red-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
            <div className="ml-auto pl-3">
              <div className="-mx-1.5 -my-1.5">
                <button
                  onClick={() => setError(null)}
                  className="inline-flex bg-red-50 rounded-md p-1.5 text-red-500 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-red-50 focus:ring-red-600"
                >
                  <span className="sr-only">Dismiss</span>
                  <svg
                    className="h-3 w-3"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Records List */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Document No
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Patient Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Age
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Blood Bag No
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Product
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Blood Group RH
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {records.map((record) => {
                const { bloodBagNo, product, bloodGroupRH } = parseASI(
                  record.asi
                );
                const isRead = readRecords.has(record.m_inoutline_id);
                const isUpdating = updatingRecords.has(record.m_inoutline_id);

                return (
                  <tr key={record.m_inoutline_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Hash className="h-4 w-4 mr-2 text-gray-400" />
                        <span className="text-sm font-medium text-gray-900">
                          {record.documentno}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <User className="h-4 w-4 mr-2 text-gray-400" />
                        <span className="text-sm text-gray-900">
                          {record.patientname}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {record.age}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {bloodBagNo}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {product}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Droplet className="h-4 w-4 mr-2 text-red-500" />
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          {bloodGroupRH}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleMarkAsUsed(record)}
                        disabled={isRead || isUpdating}
                        className={`inline-flex items-center px-3 py-1 rounded-md text-sm font-medium ${
                          isRead
                            ? "bg-green-100 text-green-800 cursor-not-allowed"
                            : isUpdating
                            ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                            : "bg-primary-600 text-white hover:bg-primary-700"
                        }`}
                      >
                        {isUpdating ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500 mr-1"></div>
                            Updating...
                          </>
                        ) : (
                          <>
                            <CheckCircle className="h-4 w-4 mr-1" />
                            {isRead ? "Used" : "Mark as Used"}
                          </>
                        )}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {records.length === 0 && (
          <div className="text-center py-12">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 font-medium">
              No shipment records found
            </p>
            <p className="text-sm text-gray-400 mt-2">
              No blood shipment records have been received yet
            </p>
          </div>
        )}
      </div>

      {/* Mark as Used Modal */}
      {showMarkAsUsedModal && selectedRecord && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={handleCloseModal}
          ></div>

          {/* Modal */}
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative w-full max-w-md transform overflow-hidden rounded-lg bg-white shadow-xl transition-all">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <CheckCircle className="h-6 w-6 text-red-500" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-lg font-semibold text-red-600">
                      Mark as Used
                    </h3>
                  </div>
                </div>
                <button
                  onClick={handleCloseModal}
                  className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Body */}
              <div className="px-6 py-4">
                {/* Header Information */}
                <div className="mb-6 space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-700">
                      Document No:
                    </span>
                    <span className="text-sm text-gray-900">
                      {selectedRecord.documentno}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-700">
                      Blood Bag No:
                    </span>
                    <span className="text-sm text-gray-900">
                      {parseASI(selectedRecord.asi).bloodBagNo}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-700">
                      Blood Group RH:
                    </span>
                    <span className="text-sm text-gray-900">
                      {parseASI(selectedRecord.asi).bloodGroupRH}
                    </span>
                  </div>
                </div>

                {/* Input Fields */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Patient Name
                    </label>
                    <input
                      type="text"
                      value={modalPatientName}
                      onChange={(e) => setModalPatientName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter patient name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Age
                    </label>
                    <input
                      type="number"
                      value={modalAge}
                      onChange={(e) => setModalAge(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter age"
                      min="0"
                      max="150"
                    />
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex justify-end space-x-3 px-6 py-4 bg-gray-50">
                <button onClick={handleCloseModal} className="btn-secondary">
                  Cancel
                </button>
                <button
                  onClick={handleConfirmMarkAsUsed}
                  disabled={!modalPatientName.trim() || !modalAge}
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InOutRecords;
